import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';

import { Button, Typography } from '@zendesk/forethought-elements';
import { ConversationReviewRating } from 'src/services/insights/types';

interface ConfirmationViewProps {
  onClose: () => void;
  onReviewNext?: () => void;
  rating: ConversationReviewRating;
}

export const ConfirmationView = ({
  onClose,
  onReviewNext,
  rating,
}: ConfirmationViewProps) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const isPositive = rating === 'good_enough';
  const emoji = isPositive ? '👍' : '👎';
  const title = isPositive
    ? t('confirmationView.positiveTitle')
    : t('confirmationView.negativeTitle');
  const subtitle = isPositive
    ? t('confirmationView.positiveSubtitle')
    : t('confirmationView.negativeSubtitle');
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        justifyContent: 'center',
        // Fill the panel vertically so the confirmation sits centered.
        // Offset roughly accounts for the panel's 40px top/bottom padding,
        // the header (~28px) and the gap above this view (~24px).
        minHeight: 'calc(100vh - 132px)',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          fontSize: '100px',
          height: '100px',
          justifyContent: 'center',
          lineHeight: 1,
          width: '100px',
        }}
      >
        {emoji}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant='font18Bold'>{title}</Typography>
        <Typography color={palette.colors.grey[700]} variant='font14'>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {/* When the conversation is the last in the flagged queue (or the
            queue is empty), the parent withholds onReviewNext so this CTA
            stays hidden — a permanently disabled CTA in a success state
            reads as broken. */}
        {onReviewNext && (
          <Button fullWidth onClick={onReviewNext} variant='main'>
            {t('confirmationView.reviewNextConversation')}
          </Button>
        )}
        <Button
          fullWidth
          onClick={onClose}
          variant={onReviewNext ? 'ghost' : 'main'}
        >
          {t('addedInstruction.closeButton')}
        </Button>
      </Box>
    </Box>
  );
};
